// =============================================================================
// calendar.ts — pure date / recurrence / layout logic for Resources → Calendar
// =============================================================================
// No I/O and no React — everything the calendar needs to decide "what shows
// where" lives here so it can be unit-tested. The UI (CalendarPage.jsx) is a thin
// renderer over these helpers.
//
// All maths is in LOCAL time (the org runs on Australia/Brisbane, which has no
// DST). Weekly recurrence steps by whole weeks via setDate(+7), so it stays on
// the same local weekday/time even if a viewer's zone did observe DST.
// =============================================================================

export interface CalendarEvent {
  id: string;
  title: string;
  staff_id: string | null;
  color: string | null;
  all_day: boolean;
  starts_at: string; // ISO instant
  ends_at: string; // ISO instant (for all-day: EXCLUSIVE end — midnight after the last day)
  recurrence: string; // "none" | "weekly"
  recur_until: string | null; // "YYYY-MM-DD" inclusive, or null
  description?: string | null;
}

// A single-instance override / cancellation of a recurring series (db-111).
// `occurrence_date` anchors it to the series instance's ORIGINAL local date; the
// non-NULL override columns replace the series values for that one instance.
export interface CalendarException {
  event_id: string;
  occurrence_date: string; // "YYYY-MM-DD"
  cancelled: boolean;
  title?: string | null;
  staff_id?: string | null;
  color?: string | null;
  all_day?: boolean | null;
  starts_at?: string | null;
  ends_at?: string | null;
  description?: string | null;
}

export interface Occurrence {
  event: CalendarEvent;
  start: Date;
  end: Date;
  allDay: boolean;
  // The series instance's ORIGINAL local date ("YYYY-MM-DD") — the stable anchor
  // for a per-instance edit/delete, even if an override moved this occurrence's
  // time. For a one-off event it's just its own start date.
  occurrenceDate: string;
}

export const DEFAULT_EVENT_COLOUR = "#5C6BC0";
const DAY_MS = 24 * 60 * 60 * 1000;
export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ── Day / week / month helpers (local time) ──────────────────────────────────
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
// Monday-based week (matches the roster screenshots).
export function startOfWeek(d: Date): Date {
  const s = startOfDay(d);
  const diff = (s.getDay() + 6) % 7; // days since Monday
  return addDays(s, -diff);
}
export function weekDays(anyDayInWeek: Date): Date[] {
  const ws = startOfWeek(anyDayInWeek);
  return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
}
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
// A 6-row (42-day) Monday-based grid covering the month d falls in.
export function monthGridDays(d: Date): Date[] {
  const gridStart = startOfWeek(startOfMonth(d));
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

// Parse a "YYYY-MM-DD" as a LOCAL date (not UTC), end-of-day for inclusivity.
function endOfLocalDate(dateStr: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
}

// Local "YYYY-MM-DD" for a Date (matches isoToLocalDate but from a Date object).
export function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Apply a not-cancelled exception's overrides to a series event, producing the
// effective event for that one instance (NULL override columns inherit).
function applyOverride(ev: CalendarEvent, exc: CalendarException): CalendarEvent {
  return {
    ...ev,
    title: exc.title ?? ev.title,
    all_day: exc.all_day ?? ev.all_day,
    description: exc.description ?? ev.description,
    // Identity/colour come straight from the instance edit (form always sets one
    // of staff_id / color), so take the exception's values directly.
    staff_id: exc.staff_id ?? null,
    color: exc.color ?? null,
  };
}

// ── Recurrence expansion ─────────────────────────────────────────────────────
// Expand `events` into concrete Occurrences overlapping [rangeStart, rangeEnd).
// One-off events pass through when they overlap; weekly events emit one instance
// per week on their base weekday/time, bounded by recur_until. Per-instance
// `exceptions` (db-111) skip a cancelled instance or override its values.
export function expandEvents(
  events: CalendarEvent[],
  rangeStart: Date,
  rangeEnd: Date,
  exceptions: CalendarException[] = []
): Occurrence[] {
  // event_id|occurrence_date -> exception
  const excByKey = new Map<string, CalendarException>();
  for (const x of exceptions || []) excByKey.set(`${x.event_id}|${x.occurrence_date}`, x);

  const out: Occurrence[] = [];
  for (const ev of events || []) {
    const baseStart = new Date(ev.starts_at);
    const baseEnd = new Date(ev.ends_at);
    const durationMs = Math.max(0, baseEnd.getTime() - baseStart.getTime());

    if (ev.recurrence === "weekly") {
      const until = ev.recur_until ? endOfLocalDate(ev.recur_until) : null;
      let cur = new Date(baseStart);
      // Fast-forward close to the range, then fine-step (bounded) — DST-safe.
      if (cur.getTime() + durationMs <= rangeStart.getTime()) {
        const approxWeeks = Math.floor((rangeStart.getTime() - cur.getTime()) / (7 * DAY_MS));
        if (approxWeeks > 0) {
          cur = new Date(cur);
          cur.setDate(cur.getDate() + approxWeeks * 7);
        }
        let guard = 0;
        while (cur.getTime() + durationMs <= rangeStart.getTime() && guard < 60) {
          cur.setDate(cur.getDate() + 7);
          guard++;
        }
      }
      let guard = 0;
      while (cur.getTime() < rangeEnd.getTime() && guard < 400) {
        guard++;
        if (until && cur.getTime() > until.getTime()) break;
        const occurrenceDate = localDateStr(cur);
        const exc = excByKey.get(`${ev.id}|${occurrenceDate}`);
        // step to the next week now, so `continue` below is safe.
        const stepped = new Date(cur);
        stepped.setDate(stepped.getDate() + 7);

        if (!exc?.cancelled) {
          const effEvent = exc ? applyOverride(ev, exc) : ev;
          const start = exc?.starts_at ? new Date(exc.starts_at) : new Date(cur);
          const end = exc?.ends_at ? new Date(exc.ends_at) : new Date(cur.getTime() + durationMs);
          if (start.getTime() < rangeEnd.getTime() && end.getTime() > rangeStart.getTime()) {
            out.push({ event: effEvent, start, end, allDay: effEvent.all_day, occurrenceDate });
          }
        }
        cur = stepped;
      }
    } else if (
      baseStart.getTime() < rangeEnd.getTime() &&
      baseEnd.getTime() > rangeStart.getTime()
    ) {
      out.push({
        event: ev,
        start: baseStart,
        end: baseEnd,
        allDay: ev.all_day,
        occurrenceDate: localDateStr(baseStart),
      });
    }
  }
  return out.sort((a, b) => a.start.getTime() - b.start.getTime());
}

// Does an occurrence touch a given calendar day?
export function occursOnDay(occ: Occurrence, day: Date): boolean {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + DAY_MS;
  return occ.start.getTime() < dayEnd && occ.end.getTime() > dayStart;
}

// The minute span [start, end] of a timed occurrence CLAMPED to a given day,
// as fractions of the day (0..1) for absolute positioning. null if it doesn't
// intersect the day.
export function daySpanFraction(
  occ: Occurrence,
  day: Date
): { topFraction: number; heightFraction: number } | null {
  if (!occursOnDay(occ, day)) return null;
  const dayStart = startOfDay(day).getTime();
  const startMin = Math.max(0, (occ.start.getTime() - dayStart) / 60000);
  const endMin = Math.min(1440, (occ.end.getTime() - dayStart) / 60000);
  const top = startMin / 1440;
  const height = Math.max(0, (endMin - startMin) / 1440);
  return { topFraction: top, heightFraction: height };
}

// ── Overlap layout (side-by-side columns) ────────────────────────────────────
// Two events at the same time must sit side by side, not on top of each other.
// This packs a day's timed occurrences into columns: events that overlap in time
// share a "cluster" and are split across N columns (N = the most that are ever
// concurrent in that cluster). Each returned item carries its vertical span plus
// a horizontal leftFraction / widthFraction (0..1 of the day column) so the UI
// can position it. Touching edges (one ends exactly as the next starts) do NOT
// count as overlapping. Pure + unit-tested; the UI stays a thin renderer.
export interface DayLayoutItem {
  occ: Occurrence;
  topFraction: number;
  heightFraction: number;
  leftFraction: number;
  widthFraction: number;
}

export function layoutDayOccurrences(occurrences: Occurrence[], day: Date): DayLayoutItem[] {
  const dayStart = startOfDay(day).getTime();
  const items: {
    occ: Occurrence;
    span: { topFraction: number; heightFraction: number };
    startMin: number;
    endMin: number;
    col: number;
    cols: number;
  }[] = [];
  for (const occ of occurrences) {
    const span = daySpanFraction(occ, day);
    if (!span) continue;
    const startMin = Math.max(0, (occ.start.getTime() - dayStart) / 60000);
    const endMin = Math.min(1440, (occ.end.getTime() - dayStart) / 60000);
    items.push({ occ, span, startMin, endMin, col: 0, cols: 1 });
  }
  // Earliest first; for equal starts, the longer one takes the left column.
  items.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  let clusterStart = 0; // index of the first item in the current cluster
  let clusterEnd = Number.NEGATIVE_INFINITY; // latest end minute seen in the cluster
  let columns: number[] = []; // per-column running end minute
  function flush(endIdx: number) {
    const cols = columns.length || 1;
    for (let i = clusterStart; i < endIdx; i++) items[i].cols = cols;
    columns = [];
    clusterEnd = Number.NEGATIVE_INFINITY;
    clusterStart = endIdx;
  }
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    // A gap (this starts at/after everything so far ended) closes the cluster.
    if (columns.length > 0 && it.startMin >= clusterEnd) flush(i);
    let c = columns.findIndex((end) => end <= it.startMin);
    if (c === -1) {
      c = columns.length;
      columns.push(it.endMin);
    } else {
      columns[c] = it.endMin;
    }
    it.col = c;
    clusterEnd = Math.max(clusterEnd, it.endMin);
  }
  flush(items.length);

  return items.map((it) => ({
    occ: it.occ,
    topFraction: it.span.topFraction,
    heightFraction: it.span.heightFraction,
    leftFraction: it.col / it.cols,
    widthFraction: 1 / it.cols,
  }));
}

// ── Colour ───────────────────────────────────────────────────────────────────
// A staff-owned event uses that person's colour; otherwise the event's own
// colour, else the default.
export function eventColour(
  ev: CalendarEvent,
  staffColourById: Record<string, string | null | undefined>
): string {
  if (ev.staff_id) {
    const c = staffColourById[ev.staff_id];
    if (c) return c;
  }
  return ev.color || DEFAULT_EVENT_COLOUR;
}

// ── Formatting ───────────────────────────────────────────────────────────────
export function formatTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h < 12 ? "am" : "pm";
  h = h % 12;
  if (h === 0) h = 12;
  return m === 0 ? `${h}${ap}` : `${h}:${String(m).padStart(2, "0")}${ap}`;
}
export function formatMonthTitle(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
// e.g. "14 – 20 Sep 2026" or "29 Sep – 5 Oct 2026" (spanning a month/year).
export function formatWeekTitle(anyDayInWeek: Date): string {
  const ws = startOfWeek(anyDayInWeek);
  const we = addDays(ws, 6);
  const mo = (x: Date) => MONTHS[x.getMonth()].slice(0, 3);
  if (ws.getMonth() === we.getMonth() && ws.getFullYear() === we.getFullYear()) {
    return `${ws.getDate()} – ${we.getDate()} ${mo(ws)} ${ws.getFullYear()}`;
  }
  if (ws.getFullYear() === we.getFullYear()) {
    return `${ws.getDate()} ${mo(ws)} – ${we.getDate()} ${mo(we)} ${ws.getFullYear()}`;
  }
  return `${ws.getDate()} ${mo(ws)} ${ws.getFullYear()} – ${we.getDate()} ${mo(we)} ${we.getFullYear()}`;
}

// ── Form <-> row conversion (all-day inclusive dates <-> exclusive end) ───────
// The form works in local date/time parts; storage keeps ISO instants with an
// EXCLUSIVE all-day end (midnight after the last day), so [start,end) covers the
// selected inclusive day range cleanly.
export function localToIso(dateStr: string, timeStr: string): string {
  const dm = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!dm) return "";
  const tm = /^(\d{2}):(\d{2})/.exec(timeStr || "00:00");
  const hh = tm ? Number(tm[1]) : 0;
  const mm = tm ? Number(tm[2]) : 0;
  return new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), hh, mm).toISOString();
}
export function isoToLocalDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function isoToLocalTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
